import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Tooltip } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import { FeatureFlag, State } from 'src/types';
import { METRIC_IDS, trackMetricTooltipViewed } from 'src/utils/analytics';
import {
  HeroMetricMetric,
  HeroMetricMetricContainer,
  HeroMetricStyled,
  HeroMetricSubtitle,
  HeroMetricTitle,
  HeroMetricTitleContainer,
  HeroMetricTitleDescriptor,
  HeroMetricTitleIcon,
  HeroMetricValue,
} from './HeroMetric.styles';

interface Props {
  title: string;
  metrics: { title: string; value: number | string }[];
  dashboard: 'organisational' | 'operational';
  tooltipText?: string;
  icon?: string;
  descriptor?: string;
  metric_id?: METRIC_IDS;
  'data-testid'?: string;
}

export const HeroMetric = ({
  title,
  tooltipText,
  metrics,
  dashboard,
  metric_id,
  descriptor = '',
  icon = undefined,
  ...props
}: Props) => {
  const theme = useTheme();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [trackCalled, setTrackCalled] = useState(false);

  const features = useSelector((state: State) => state.features);
  const organisation = useSelector(
    (state: State) => state.organisation?.organisation,
  );
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  return (
    <>
      <HeroMetricStyled {...props}>
        <HeroMetricTitleContainer>
          <HeroMetricTitle title={`${title} ${descriptor}`}>
            {icon && (
              <HeroMetricTitleIcon
                className={icon}
                size="22px"
                data-testid={props['data-testid']}
              />
            )}
            {title}
            {descriptor && (
              <HeroMetricTitleDescriptor>
                {descriptor}
              </HeroMetricTitleDescriptor>
            )}
          </HeroMetricTitle>
          {tooltipText && (
            <StyledIcon
              className={'uil uil-info-circle'}
              onMouseOver={() => {
                setTooltipVisible(true);
                if (
                  organisation &&
                  segmentEnabled &&
                  !trackCalled &&
                  metric_id
                ) {
                  setTrackCalled(true);
                  trackMetricTooltipViewed(
                    organisation.id,
                    dashboard,
                    metric_id,
                  );
                  setTimeout(() => setTrackCalled(false), 5000);
                }
              }}
              onMouseOut={() => setTooltipVisible(false)}
              ref={setRoot}
              color={
                tooltipVisible
                  ? theme.atlas.semantic.color.action.primary.default
                  : theme.atlas.semantic.color.type.default
              }
              size="20px"
              data-testid="hero-metric-information"
            />
          )}
        </HeroMetricTitleContainer>
        <Divider variant="horizontal" />
        <HeroMetricMetricContainer>
          {metrics.map((metric, i) => (
            <HeroMetricMetric
              data-testid={`hero-metric-metric-${i}`}
              key={metric.title}
            >
              <HeroMetricSubtitle title={metric.title}>
                {metric.title}
              </HeroMetricSubtitle>
              <HeroMetricValue title={String(metric.value)}>
                {metric.value}
              </HeroMetricValue>
            </HeroMetricMetric>
          ))}
        </HeroMetricMetricContainer>
      </HeroMetricStyled>
      {tooltipVisible && (
        <Tooltip
          text={tooltipText}
          variant="down"
          root={root as HTMLElement}
          data-testid="hero-metric-tooltip"
        />
      )}
    </>
  );
};
